import { Divider } from 'antd'
import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import { useCallback, useContext } from 'react'

import { ReactComponent as ClearFormattingIcon } from 'assets/icons/clearFormatting32.svg'
import { ReactComponent as ExpandFormattingOptionsIcon } from 'assets/icons/expandFormattingOptions32.svg'
import { BASIC_HIGHLIGHT_COLOR_OPTIONS, HIGHLIGHT_COLOR_OPTIONS } from 'common/Prose/constants/colorOptions'
import { useEditorView } from 'common/Prose/EditorProvider/hooks/useEditorView'
import { Button } from 'components/Button'
import { AlmanacEditorStateContext } from 'stores/EditorState'

import { removeHighlightColor, setHighlightColor } from 'common/Prose/Commands/formatting/highlightColor'
import { keyboardHeight } from 'utils/virtualKeyboardOverlap'
import { SelfScrollingButton } from '../components/utils/SelfScrollingButton'
import { useMobileToolbar } from '../stores/MobileToolbarStore'
import Styles from './TextHighlightSelection.module.scss'

function isHighlightBtn(colorStr: string) {
  return !/gradient/.test(colorStr)
}

export const TextHighlightSelectionBar = observer(() => {
  const mobileToolbarStore = useMobileToolbar()
  const view = useEditorView()

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { highlightColors: activeHighlights } = useContext(AlmanacEditorStateContext)!
  const noColorSelected = !HIGHLIGHT_COLOR_OPTIONS.some((color) => activeHighlights.includes(color))

  return (
    <div className={Styles.bar}>
      <Button
        preventFocusOnPress
        variant="none"
        size="sm"
        key={'expand-options'}
        aria-label="Expand highlight color options"
        className={cx(Styles.barItem, Styles.extraButton)}
        onClick={() => {
          mobileToolbarStore.toggleDialog()
        }}
        data-testid="mobile-toolbar-text-highlight-expand-options"
      >
        <ExpandFormattingOptionsIcon />
      </Button>
      <SelfScrollingButton
        isActive={noColorSelected}
        key={'clear-formatting'}
        aria-label="Clear highlight formatting"
        className={cx(Styles.barItem, Styles.clearFormatting, {
          [Styles.activeItem]: noColorSelected,
        })}
        onClick={() => {
          removeHighlightColor(view)
          mobileToolbarStore.closeDialog()
        }}
        data-testid="mobile-toolbar-highlight-clear-formatting"
      >
        <ClearFormattingIcon />
      </SelfScrollingButton>
      <Divider type="vertical" className={Styles.divider} />
      {Object.entries(BASIC_HIGHLIGHT_COLOR_OPTIONS).map(([key, color], n) => (
        <SelfScrollingButton
          isActive={activeHighlights.includes(color)}
          key={key}
          aria-label={`Set highlight color to basic option ${n + 1}`}
          className={Styles.button}
          onClick={() => {
            setHighlightColor(color, view)
            mobileToolbarStore.closeDialog()
          }}
          data-testid={`mobile-toolbar-highlight-color-${n}`}
        >
          <span
            style={{ background: color }}
            className={cx(Styles.barItem, Styles.colorButton, {
              [Styles.activeItem]: activeHighlights.includes(color),
              [Styles.colorButton]: isHighlightBtn(color),
              [Styles.gradientHighlight]: !isHighlightBtn(color),
            })}
          >
            &nbsp;
          </span>
        </SelfScrollingButton>
      ))}
    </div>
  )
})

export const TextHighlightSelectionDialog = observer(() => {
  const mobileToolbarStore = useMobileToolbar()
  const view = useEditorView()

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { highlightColors: activeHighlights } = useContext(AlmanacEditorStateContext)!

  const selectColor = useCallback(
    (color: string) => () => {
      setHighlightColor(color, view)
      mobileToolbarStore.closeDialog()
    },
    [view, mobileToolbarStore],
  )

  const windowHeight = window.visualViewport?.height ?? window.innerHeight
  /* Navbar + Border + MobileToolbar 1st Tier + border + MobileToolbar 2nd Tier */
  const otherContent = 48 + 1 + 48 + 1 + 48
  const availableDialogHeight = windowHeight - keyboardHeight() - otherContent

  return (
    <div className={Styles.dialog} style={{ maxHeight: `${availableDialogHeight}px` }}>
      {Object.entries(HIGHLIGHT_COLOR_OPTIONS).map(([key, color], n) => (
        <div className={Styles.dialogItem} key={key}>
          <Button
            preventFocusOnPress
            variant="none"
            size="sm"
            aria-label={`Set highlight color to advanced option ${n + 1}`}
            onClick={selectColor(color)}
            className={Styles.button}
            data-testid={`mobile-toolbar-highlight-color-${n}`}
          >
            <span
              style={{ background: color }}
              className={cx(Styles.colorButton, {
                [Styles.activeItem]: activeHighlights.includes(color),
                [Styles.colorButton]: isHighlightBtn(color),
                [Styles.gradientHighlight]: !isHighlightBtn(color),
              })}
            >
              &nbsp;
            </span>
          </Button>
        </div>
      ))}
    </div>
  )
})
